import { LocalDate } from "@js-joda/core";
import type { SearchableDropdownOption } from "components/designV2/searchableDropdown/searchableDropdown.types";
import type {
  EmploymentTemplate,
  EmploymentTemplateSalary,
} from "services/api/company/staff.service";
import type { UnitTeamOption } from "web-apps/company/components/common/filters/unitTeamDropdown.component";
import {
  filterTemplateSalariesByAge,
  toTemplateEnumOrFallback,
} from "web-apps/company/components/staff/components/shared/staffEmploymentTemplate.logic";
import type { EmploymentDetailsForm } from "../profilePanel.contract";

const toLatestDate = (first: LocalDate, second: LocalDate): LocalDate => {
  return first.isAfter(second) ? first : second;
};

export const toDefaultSalaryChangeDate = (
  nowDate: LocalDate,
  employmentStartDate: LocalDate | null,
): LocalDate => {
  const baselineDate =
    employmentStartDate == null
      ? nowDate
      : toLatestDate(nowDate, employmentStartDate);

  if (baselineDate.dayOfMonth() === 1) {
    return baselineDate;
  }

  const nextMonthDate = baselineDate.plusMonths(1);
  return LocalDate.of(nextMonthDate.year(), nextMonthDate.monthValue(), 1);
};

export const applyEmploymentTemplateToEmploymentDetails = (
  details: EmploymentDetailsForm,
  template: EmploymentTemplate,
): EmploymentDetailsForm => {
  return {
    ...details,
    employmentType: String(
      toTemplateEnumOrFallback(template.employmentType, details.employmentType),
    ),
    salaryType: String(
      toTemplateEnumOrFallback(template.salaryType, details.salaryType),
    ),
    employeeCategory: String(
      toTemplateEnumOrFallback(
        template.employeeCategory,
        details.employeeCategory,
      ),
    ),
    paymentType: String(
      toTemplateEnumOrFallback(template.paymentType, details.paymentType),
    ),
    role: template.role ?? details.role,
    salary:
      template.salaryValue == null
        ? details.salary
        : String(template.salaryValue),
    workPercentage:
      template.workPercentage == null
        ? details.workPercentage
        : String(Math.round(template.workPercentage * 100)),
    agreement: template.agreement ?? details.agreement,
    noticeDays:
      template.noticeDays == null
        ? details.noticeDays
        : String(template.noticeDays),
    vacationDays:
      template.vacationDays == null
        ? details.vacationDays
        : String(template.vacationDays),
    workplace: template.workplace ?? details.workplace,
  };
};

export const toTeamOptions = (
  unitTeamOptions: UnitTeamOption[],
): SearchableDropdownOption[] => {
  return unitTeamOptions.flatMap((unit): SearchableDropdownOption[] => {
    const unitName = (unit.unitName ?? "").trim();

    return unit.teams.map((team) => {
      const teamName = (team.name ?? "").trim();
      return {
        value: String(team.id),
        label: unitName.length > 0 ? `${unitName} - ${teamName}` : teamName,
      };
    });
  });
};

export const toVisibleTemplateSalaries = (
  templateSalaries: EmploymentTemplateSalary[],
  workerAge: number | null,
): EmploymentTemplateSalary[] => {
  return filterTemplateSalariesByAge(templateSalaries, workerAge);
};
